import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { contractTemplatesApi, jvAgreementsApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  Save, Download, RotateCcw, Loader2, FileText, ArrowUpRight,
  Upload, CheckCircle2, ExternalLink,
} from 'lucide-react';
import { jsPDF } from 'jspdf';

// Matches an unfilled merge field ({{...}}) or a blank fill line (4+ underscores).
const BLANK_RE = /(\{\{[^}]+\}\}|_{4,})/g;

// Render a normal (non-bold) text segment, highlighting any remaining blanks
// so the owner can immediately see what still needs manual input.
function renderBlanks(text, keyPrefix) {
  const nodes = [];
  let last = 0;
  let idx = 0;
  let m;
  BLANK_RE.lastIndex = 0;
  while ((m = BLANK_RE.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(<span key={`${keyPrefix}-t${idx}`}>{text.slice(last, m.index)}</span>);
    }
    nodes.push(
      <span
        key={`${keyPrefix}-b${idx}`}
        className="bg-amber-100 text-amber-800 rounded-[2px] px-1"
        data-testid="contract-blank"
      >
        {m[0].startsWith('{{') ? m[0] : '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0'}
      </span>,
    );
    last = m.index + m[0].length;
    idx += 1;
  }
  if (last < text.length) {
    nodes.push(<span key={`${keyPrefix}-t${idx}`}>{text.slice(last)}</span>);
  }
  return nodes;
}

function PreviewLine({ line, lineKey, isTitle }) {
  const trimmed = line.trim();
  if (isTitle) {
    return (
      <div className="text-center font-bold text-sm tracking-wide">
        {trimmed.replace(/\*\*/g, '') || '\u00A0'}
      </div>
    );
  }
  const heading = /^\*\*(.+)\*\*$/.exec(trimmed);
  if (heading) {
    return <div className="font-bold text-foreground mt-3 mb-0.5">{heading[1]}</div>;
  }
  if (trimmed === '') return <div className="h-2" aria-hidden="true" />;

  // Inline: segments at odd indices were auto-filled (wrapped in ** by backend).
  const segs = line.split('**');
  return (
    <div className="whitespace-pre-wrap">
      {segs.map((seg, i) =>
        i % 2 === 1 ? (
          <span
            key={`${lineKey}-f${i}`}
            className="font-semibold bg-emerald-100 text-emerald-900 rounded-[2px] px-0.5"
            data-testid="contract-filled"
          >
            {seg}
          </span>
        ) : (
          <React.Fragment key={`${lineKey}-n${i}`}>
            {renderBlanks(seg, `${lineKey}-n${i}`)}
          </React.Fragment>
        ),
      )}
    </div>
  );
}

function ContractPreview({ body }) {
  const lines = (body || '').split('\n');
  return (
    <div
      className="border rounded-sm p-4 bg-white max-h-[440px] overflow-y-auto text-[12.5px] leading-relaxed"
      data-testid="contract-preview"
    >
      {lines.map((line, i) => (
        <PreviewLine key={`l${i}`} lineKey={`l${i}`} line={line} isTitle={i < 2} />
      ))}
    </div>
  );
}

/**
 * Per-deal contract editor.
 *
 * Loads the appropriate master template, renders merge fields with the deal's
 * data, lets the admin tweak deal-specific clauses, persists the draft on the
 * JV agreement, and downloads a PDF on demand. Replaces all GHL flows.
 */
export function DealContractEditor({ deal, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingSigned, setUploadingSigned] = useState(false);
  const [body, setBody] = useState('');
  const [viewMode, setViewMode] = useState('preview');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [meta, setMeta] = useState(null);
  const signedFileRef = useRef(null);

  const load = useCallback(async () => {
    if (!deal) return;
    setLoading(true);
    try {
      const res = await contractTemplatesApi.getDealContract(deal.id);
      const d = res.data;
      setBody(d.rendered_body || '');
      setAvailableTemplates(d.available_templates || []);
      setCurrentTemplateId(d.current_template_id);
      setMeta({
        deal: d.deal,
        partner: d.partner,
        jv: d.jv_agreement,
        is_saved_draft: d.is_saved_draft,
        current_template_name: d.current_template_name,
        signed_document_url: d.jv_agreement?.signed_document_url || null,
        signed_document_filename: d.jv_agreement?.signed_document_filename || null,
        signing_status: d.jv_agreement?.signing_status || null,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, [deal]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const switchTemplate = async (newTemplateId) => {
    if (newTemplateId === currentTemplateId) return;
    if (meta?.is_saved_draft) {
      const ok = window.confirm(
        'Switching templates will replace your current draft with a fresh render of the new template. Continue?',
      );
      if (!ok) return;
      // Reset draft first
      try {
        await contractTemplatesApi.resetDealContract(deal.id);
      } catch {
        toast.error('Failed to reset draft');
        return;
      }
    }
    // Save the chosen template_id (with empty draft so the next load re-renders)
    try {
      await contractTemplatesApi.saveDealContract(deal.id, {
        template_id: newTemplateId,
        body: '',
      });
      // Now reset so next load renders fresh
      await contractTemplatesApi.resetDealContract(deal.id);
      // Save the chosen template id again WITHOUT body so it sticks
      await contractTemplatesApi.saveDealContract(deal.id, {
        template_id: newTemplateId,
        body: '',
      });
      await contractTemplatesApi.resetDealContract(deal.id);
      // Persist preference: write template_id but no draft
      // (next GET will populate fresh from this template)
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch template');
    }
  };

  const saveDraft = async () => {
    if (!deal) return;
    setSaving(true);
    try {
      await contractTemplatesApi.saveDealContract(deal.id, {
        template_id: currentTemplateId,
        body,
      });
      toast.success('Draft saved');
      setMeta(m => ({ ...m, is_saved_draft: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetDraft = async () => {
    if (!window.confirm('Discard your edits and re-render from the master template?')) return;
    try {
      await contractTemplatesApi.resetDealContract(deal.id);
      await load();
      toast.success('Reverted to master template');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset');
    }
  };

  const downloadPdf = async () => {
    if (!body.trim()) {
      toast.error('Contract is empty');
      return;
    }

    // Save draft first so what they download matches what's stored
    await saveDraft();

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 54;
    const marginTop = 64;
    const marginBottom = 54;
    const usableWidth = pageWidth - marginX * 2;

    let cursorY = marginTop;
    const lineHeight = 13;
    const paragraphGap = 8;

    // Split into paragraphs preserving blank lines as section breaks
    const paragraphs = body.split(/\n\s*\n/);

    const ensureSpace = (extra = lineHeight) => {
      if (cursorY + extra > pageHeight - marginBottom) {
        doc.addPage();
        cursorY = marginTop;
      }
    };

    // Draw a normal paragraph line with inline **bold** support + word wrap.
    const drawRichLine = (line) => {
      doc.setFontSize(10.5);
      const segs = line.split('**');
      const words = [];
      segs.forEach((seg, si) => {
        const bold = si % 2 === 1;
        seg.split(/(\s+)/).forEach((tok) => {
          if (tok.length) words.push({ t: tok, bold });
        });
      });
      if (words.length === 0) {
        ensureSpace();
        cursorY += lineHeight;
        return;
      }
      let lineArr = [];
      let lineW = 0;
      const flushLine = () => {
        ensureSpace();
        let x = marginX;
        lineArr.forEach((w) => {
          doc.setFont('helvetica', w.bold ? 'bold' : 'normal');
          doc.text(w.t, x, cursorY);
          x += w.ww;
        });
        cursorY += lineHeight;
        lineArr = [];
        lineW = 0;
      };
      words.forEach((w) => {
        doc.setFont('helvetica', w.bold ? 'bold' : 'normal');
        const ww = doc.getTextWidth(w.t);
        if (lineW + ww > usableWidth && lineArr.length > 0) flushLine();
        if (lineArr.length === 0 && w.t.trim() === '') return; // drop leading space after wrap
        lineArr.push({ ...w, ww });
        lineW += ww;
      });
      if (lineArr.length) flushLine();
    };

    paragraphs.forEach((para, idx) => {
      const lines = para.split('\n');
      lines.forEach((line) => {
        const trimmed = line.trim();
        const isHeading = /^\*\*.+\*\*$/.test(trimmed);

        if (idx === 0 && para === paragraphs[0] && lines.indexOf(line) < 2) {
          // Title block — center, bold, larger
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(15);
          ensureSpace(20);
          doc.text(trimmed.replace(/\*\*/g, '') || ' ', pageWidth / 2, cursorY, { align: 'center' });
          cursorY += 18;
        } else if (isHeading) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          const cleanLine = trimmed.replace(/^\*\*/, '').replace(/\*\*$/, '').replace(/\*\*/g, '');
          const wrapped = doc.splitTextToSize(cleanLine, usableWidth);
          wrapped.forEach((w) => {
            ensureSpace();
            doc.text(w, marginX, cursorY);
            cursorY += lineHeight;
          });
        } else {
          drawRichLine(line);
        }
      });
      cursorY += paragraphGap;
    });

    // Footer page numbers
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(
        `Page ${p} of ${totalPages}`,
        pageWidth - marginX,
        pageHeight - 30,
        { align: 'right' },
      );
    }

    const safeName = (meta?.deal?.name || 'Contract').replace(/[^\w\-]+/g, '_');
    doc.save(`JV_Agreement_${safeName}.pdf`);
    toast.success('Contract downloaded');
  };

  const uploadSignedContract = async (file) => {
    if (!file) return;
    if (!meta?.jv?.id) {
      toast.error('Save the draft first so we have a contract record to attach to');
      return;
    }
    setUploadingSigned(true);
    try {
      await jvAgreementsApi.uploadSigned(meta.jv.id, file);
      toast.success('Signed contract uploaded');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingSigned(false);
      if (signedFileRef.current) signedFileRef.current.value = '';
    }
  };

  if (!open || !deal) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="deal-contract-editor"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="w-4 h-4 text-[#C68E5D]" />
            Contract — {meta?.deal?.name || deal.summary?.project_name}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Edit the agreement for this deal, save your draft, then download the PDF and upload it to your e-signature provider.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading template...
          </div>
        ) : (
          <div className="space-y-3">
            {/* Template selector + status */}
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Template:</Label>
                <Select
                  value={currentTemplateId || ''}
                  onValueChange={switchTemplate}
                >
                  <SelectTrigger className="h-8 text-xs rounded-sm w-auto min-w-[200px]" data-testid="contract-template-select">
                    <SelectValue placeholder="Choose template" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} {t.is_default_for_type && <span className="text-[10px] text-emerald-600 ml-1">(default)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <a
                href="/templates"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-[#C68E5D] hover:underline inline-flex items-center gap-1"
              >
                <ArrowUpRight className="w-3 h-3" />
                Edit master templates
              </a>
              {meta?.is_saved_draft ? (
                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 ml-auto">
                  Saved draft (deal-specific edits)
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50 ml-auto">
                  Fresh from template
                </Badge>
              )}
            </div>

            {/* View toggle + highlight legend */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="inline-flex rounded-sm border overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setViewMode('preview')}
                  className={`px-3 py-1 ${viewMode === 'preview' ? 'bg-[#C68E5D] text-white' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                  data-testid="contract-view-preview"
                >
                  Preview
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('edit')}
                  className={`px-3 py-1 border-l ${viewMode === 'edit' ? 'bg-[#C68E5D] text-white' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                  data-testid="contract-view-edit"
                >
                  Edit
                </button>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-[2px] bg-emerald-100 border border-emerald-300" /> Auto-filled
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-[2px] bg-amber-100 border border-amber-300" /> Needs your input
                </span>
              </div>
            </div>

            {/* Preview (highlighted) or raw editor */}
            {viewMode === 'preview' ? (
              <ContractPreview body={body} />
            ) : (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full font-mono text-[12.5px] border rounded-sm p-3 bg-background leading-relaxed focus:outline-none focus:ring-1 focus:ring-ring"
                rows={26}
                spellCheck={false}
                data-testid="contract-body-textarea"
              />
            )}

            <p className="text-[11px] text-muted-foreground">
              Auto-filled values from the proposal are shown <span className="font-semibold text-emerald-700">highlighted</span>. Anything <span className="text-amber-700">amber</span> (a blank line or <code className="bg-muted px-1 rounded-sm">{'{{double_braces}}'}</code>) needs your input — switch to <span className="font-medium">Edit</span> to fill it in or update the project record.
            </p>

            {/* Signed contract slot */}
            <div className={`rounded-sm border p-3 ${meta?.signed_document_url ? 'border-emerald-200 bg-emerald-50/40' : 'border-dashed border-border bg-muted/20'}`}>
              {meta?.signed_document_url ? (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-emerald-800">Signed contract on file</div>
                      <div className="text-[11px] text-emerald-700/80 truncate">
                        {meta.signed_document_filename || 'signed-jv.pdf'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 rounded-sm gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      data-testid="view-signed-contract-btn"
                    >
                      <a href={meta.signed_document_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 rounded-sm gap-1.5 text-muted-foreground"
                      onClick={() => signedFileRef.current?.click()}
                      disabled={uploadingSigned}
                      data-testid="replace-signed-contract-btn"
                    >
                      <Upload className="w-3 h-3" /> Replace
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs">
                    <div className="font-medium">After signing</div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">
                      Once your e-signature provider returns the executed PDF, upload it here so it lives in the Documents Vault and the investor's portal.
                    </div>
                  </div>
                  <Button
                    onClick={() => signedFileRef.current?.click()}
                    disabled={uploadingSigned}
                    variant="outline"
                    size="sm"
                    className="text-xs h-8 rounded-sm gap-1.5 flex-shrink-0"
                    data-testid="upload-signed-contract-btn"
                  >
                    {uploadingSigned ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Upload Signed PDF
                  </Button>
                </div>
              )}
              <input
                type="file"
                ref={signedFileRef}
                onChange={(e) => uploadSignedContract(e.target.files?.[0])}
                accept=".pdf,application/pdf"
                className="hidden"
                data-testid="signed-contract-file-input"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={resetDraft}
            disabled={loading || !meta?.is_saved_draft}
            className="rounded-sm gap-1.5 text-xs"
            data-testid="reset-contract-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to Template
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-sm"
          >
            Close
          </Button>
          <Button
            onClick={saveDraft}
            disabled={loading || saving}
            variant="outline"
            className="rounded-sm gap-1.5"
            data-testid="save-contract-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Draft
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={loading || !body}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm gap-1.5"
            data-testid="download-contract-btn"
          >
            <Download className="w-3.5 h-3.5" />
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
